import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FitConfigProviderService } from '@shared/fit-config-provider.service';
import {
    BroadcastAttachment,
    BroadcastAudience,
    BroadcastAudienceMember,
    BroadcastMessage,
    ResolutionCenterService,
} from '../resolution-center.service';
import { FitList } from '@shared/fit-api-client.service';

@Component({
    selector: 'app-broadcast-messages',
    templateUrl: './broadcast-messages.component.html',
    styleUrls: ['./broadcast-messages.component.scss'],
    standalone: false,
})

export class BroadcastMessagesComponent implements OnInit, OnDestroy {
    constructor(
        private resolutionCenterService: ResolutionCenterService,
        private fitConfigProviderService: FitConfigProviderService,
        private router: Router,
    ) {}

    get isCoach() {
        return this.fitConfigProviderService.isCoach;
    }

    public audiencesLoading = true;
    public systemAudiences: BroadcastAudience[] = [];
    public customAudiences: BroadcastAudience[] = [];
    public audienceSearch = '';

    public selectedAudience: BroadcastAudience | null = null;
    public editingBroadcastMessage: BroadcastMessage | null = null;

    public messagesLoading = false;
    public messages: BroadcastMessage[] = [];
    public messagesList: FitList<BroadcastMessage> | null = null;
    private messagesPage = 1;

    /** Light poll for status / recipient_count updates (chat messages poll every 30s). */
    private broadcastListPollIntervalId: ReturnType<typeof setInterval> | null = null;
    private readonly BROADCAST_LIST_POLL_INTERVAL_MS = 60_000;
    private broadcastListPollInFlight = false;

    showImageModal = false;
    isClosingImageModal = false;
    selectedMedia: { image_url: string; label: string } | null = null;

    public memberSearchQuery = '';
    public audienceMembers: BroadcastAudienceMember[] = [];
    public membersLoading = false;
    public membersHasMorePages = false;
    private memberSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    openMemberMenuId: number | null = null;
    submenuPosition: { top: number; left: number } | null = null;

    showManageMembersModal = false;

    get hasSelectedAudienceMembers(): boolean {
        const n = this.selectedAudience?.member_count;
        return (n ?? 0) > 0;
    }

    ngOnInit(): void {
        this.loadAudiences();
    }

    ngOnDestroy(): void {
        this.stopBroadcastListPoll();
        if (this.memberSearchDebounceTimer) {
            clearTimeout(this.memberSearchDebounceTimer);
            this.memberSearchDebounceTimer = null;
        }
    }

    get filteredSystem(): BroadcastAudience[] {
        return this.systemAudiences.filter((a) => this.audienceMatchesSearch(a));
    }

    get filteredCustom(): BroadcastAudience[] {
        return this.customAudiences.filter((a) => this.audienceMatchesSearch(a));
    }

    get hasMoreMessages(): boolean {
        if (!this.messagesList) {
            return false;
        }
        return this.messagesList.current_page < this.messagesList.last_page;
    }

    async loadAudiences(): Promise<void> {
        this.audiencesLoading = true;
        const { data, error } = await this.resolutionCenterService.getBroadcastAudiences();
        this.audiencesLoading = false;
        if (error || !data) {
            this.systemAudiences = [];
            this.customAudiences = [];
            return;
        }
        this.systemAudiences = data.system ?? [];
        this.customAudiences = data.custom ?? [];
    }

    selectAudience(audience: BroadcastAudience): void {
        if (this.selectedAudience?.audience_key === audience.audience_key) {
            return;
        }
        this.stopBroadcastListPoll();
        this.selectedAudience = audience;
        this.editingBroadcastMessage = null;
        this.messagesPage = 1;
        this.messages = [];
        this.messagesList = null;
        this.resetAudienceMembers();
        void this.loadMessages(false).then(() => this.startBroadcastListPoll());
        void this.loadAudienceMembers();
    }

    async loadMessages(append: boolean): Promise<void> {
        if (!this.selectedAudience) {
            return;
        }
        this.messagesLoading = true;
        const page = append ? this.messagesPage + 1 : 1;
        const { data, error } = await this.resolutionCenterService.getBroadcastMessages(
            this.selectedAudience.audience_key,
            page,
        );
        this.messagesLoading = false;
        if (error || !data) {
            if (!append) {
                this.messages = [];
                this.messagesList = null;
            }
            return;
        }
        this.messagesList = data;
        this.messagesPage = data.current_page;
        const incoming = (data.data ?? []) as BroadcastMessage[];
        // API may return newest-first; match messages-tab / client center: chronological (oldest top, latest bottom).
        const merged = append ? [...this.messages, ...incoming] : [...incoming];
        this.messages = merged.sort((a, b) => a.id - b.id);
    }

    loadMoreMessages(): void {
        if (!this.hasMoreMessages || this.messagesLoading) {
            return;
        }
        void this.loadMessages(true);
    }

    clearAudienceSearch(): void {
        this.audienceSearch = '';
    }

    getAudienceIconUrl(audience: BroadcastAudience): string {
        return audience.type === 'custom'
            ? 'assets/messaging/ic_users_custom_groups.svg'
            : 'assets/messaging/ic_users_system_groups.svg';
    }

    onMemberSearchChange(): void {
        if (this.memberSearchDebounceTimer) {
            clearTimeout(this.memberSearchDebounceTimer);
        }
        this.memberSearchDebounceTimer = setTimeout(() => {
            void this.loadAudienceMembers();
            this.memberSearchDebounceTimer = null;
        }, 300);
    }

    onSeeAllMembers(event: Event): void {
        event.preventDefault();
        this.showManageMembersModal = true;
    }

    onCloseManageMembersModal(): void {
        this.showManageMembersModal = false;
    }

    getMemberInitials(member: BroadcastAudienceMember): string {
        const name = (member.name || '').trim();
        if (!name) {
            return '?';
        }
        const words = name.split(/\s+/);
        if (words.length >= 2) {
            return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
        }
        return words[0].charAt(0).toUpperCase();
    }

    @HostListener('document:click')
    closeMemberMenu(): void {
        this.openMemberMenuId = null;
        this.submenuPosition = null;
    }

    onToggleMemberMenu(event: Event, member: BroadcastAudienceMember): void {
        event.stopPropagation();
        if (this.openMemberMenuId === member.id) {
            this.openMemberMenuId = null;
            this.submenuPosition = null;
            return;
        }
        const el = (event.target as HTMLElement).closest('.action') as HTMLElement;
        if (el) {
            const rect = el.getBoundingClientRect();
            const submenuWidth = 180;
            this.submenuPosition = {
                top: rect.bottom + 4,
                left: Math.max(8, rect.right - submenuWidth - 4),
            };
        }
        this.openMemberMenuId = member.id;
    }

    getOpenMember(): BroadcastAudienceMember | null {
        if (this.openMemberMenuId == null) {
            return null;
        }
        return this.audienceMembers.find((m) => m.id === this.openMemberMenuId) ?? null;
    }

    onOpenMemberProfile(member: BroadcastAudienceMember): void {
        if (!member?.id) {
            return;
        }
        window.open(`/user/user/details-mantis/${member.id}`, '_blank');
        this.openMemberMenuId = null;
        this.submenuPosition = null;
    }

    onMessageMember(member: BroadcastAudienceMember): void {
        if (!member?.id) {
            return;
        }
        this.resolutionCenterService.openDirectChatRequest = { userId: member.id };
        this.router.navigate(['/client-messages']);
        this.openMemberMenuId = null;
        this.submenuPosition = null;
    }

    private resetAudienceMembers(): void {
        this.memberSearchQuery = '';
        this.audienceMembers = [];
        this.membersHasMorePages = false;
        this.membersLoading = false;
        this.openMemberMenuId = null;
        this.submenuPosition = null;
        this.showManageMembersModal = false;
        if (this.memberSearchDebounceTimer) {
            clearTimeout(this.memberSearchDebounceTimer);
            this.memberSearchDebounceTimer = null;
        }
    }

    private async loadAudienceMembers(): Promise<void> {
        if (!this.selectedAudience) {
            this.audienceMembers = [];
            this.membersHasMorePages = false;
            return;
        }

        this.membersLoading = true;
        const query = this.memberSearchQuery?.trim() || '';
        const { data, error } = await this.resolutionCenterService.getBroadcastMembers(
            this.selectedAudience.audience_key,
            query || undefined,
            1,
        );
        this.membersLoading = false;

        if (error || !data) {
            this.audienceMembers = [];
            this.membersHasMorePages = false;
            return;
        }

        this.audienceMembers = data.data ?? [];
        this.membersHasMorePages = data.last_page > 1;
    }

    statusBadgeNgStyle(badge: BroadcastMessage['status_badge']): { [key: string]: string } {
        if (!badge) {
            return {};
        }
        const hex = this.normalizeStatusBadgeHex(badge.color);
        if (!hex || this.isLightStatusBadgeColor(hex)) {
            return {};
        }
        return {
            color: hex,
            'border-color': hex,
            'background-color': `${hex}1a`,
        };
    }

    /** API may send #fff or other light colors — skip inline styles and use SCSS fallbacks by key. */
    private normalizeStatusBadgeHex(color: string | undefined): string | null {
        if (!color?.trim()) {
            return null;
        }
        let c = color.trim();
        if (!c.startsWith('#')) {
            return null;
        }
        c = c.slice(1);
        if (c.length === 3) {
            c = c.split('').map((ch) => ch + ch).join('');
        }
        if (!/^[0-9a-fA-F]{6}$/.test(c)) {
            return null;
        }
        return `#${c.toLowerCase()}`;
    }

    private isLightStatusBadgeColor(hex: string): boolean {
        const h = hex.replace('#', '');
        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.72;
    }

    private audienceMatchesSearch(audience: BroadcastAudience): boolean {
        const q = this.audienceSearch.trim().toLowerCase();
        if (!q) {
            return true;
        }
        const name = (audience.name || '').toLowerCase();
        const desc = (audience.description || '').toLowerCase();
        return name.includes(q) || desc.includes(q);
    }

    canEditBroadcastMessage(msg: BroadcastMessage): boolean {
        if (!this.hasSelectedAudienceMembers) {
            return false;
        }
        if (msg.is_sent) {
            return false;
        }
        const status = (msg.status || msg.status_badge?.key || '').toLowerCase();
        return status !== 'sent';
    }

    startEditBroadcastMessage(msg: BroadcastMessage): void {
        if (!this.canEditBroadcastMessage(msg)) {
            return;
        }
        this.editingBroadcastMessage = msg;
    }

    onBroadcastMessageUpserted(msg: BroadcastMessage): void {
        this.upsertBroadcastInList(msg);
        this.editingBroadcastMessage = null;
    }

    onBroadcastEditCancelled(): void {
        this.editingBroadcastMessage = null;
    }

    onBroadcastMessageRemoved(id: number): void {
        this.messages = this.messages.filter((m) => m.id !== id);
        if (this.editingBroadcastMessage?.id === id) {
            this.editingBroadcastMessage = null;
        }
    }

    private upsertBroadcastInList(msg: BroadcastMessage): void {
        this.mergeBroadcastMessagesIntoList([msg]);
    }

    private startBroadcastListPoll(): void {
        this.stopBroadcastListPoll();
        if (!this.selectedAudience?.audience_key) {
            return;
        }
        this.broadcastListPollIntervalId = setInterval(() => {
            void this.refreshBroadcastList();
        }, this.BROADCAST_LIST_POLL_INTERVAL_MS);
    }

    private stopBroadcastListPoll(): void {
        if (this.broadcastListPollIntervalId != null) {
            clearInterval(this.broadcastListPollIntervalId);
            this.broadcastListPollIntervalId = null;
        }
    }

    /** Re-fetch loaded pages and merge by id so status badge and recipient count stay current. */
    private async refreshBroadcastList(): Promise<void> {
        if (!this.selectedAudience?.audience_key || this.messagesLoading || this.broadcastListPollInFlight) {
            return;
        }

        this.broadcastListPollInFlight = true;
        const audienceKey = this.selectedAudience.audience_key;
        const pagesToRefresh = Math.max(1, this.messagesPage);

        try {
            for (let page = 1; page <= pagesToRefresh; page++) {
                const { data, error } = await this.resolutionCenterService.getBroadcastMessages(
                    audienceKey,
                    page,
                );
                if (error || !data) {
                    return;
                }
                this.mergeBroadcastMessagesIntoList((data.data ?? []) as BroadcastMessage[]);
            }
        } finally {
            this.broadcastListPollInFlight = false;
        }
    }

    private mergeBroadcastMessagesIntoList(incoming: BroadcastMessage[]): void {
        if (!incoming.length) {
            return;
        }
        const byId = new Map(this.messages.map((m) => [m.id, m]));
        for (const msg of incoming) {
            byId.set(msg.id, { ...msg });
        }
        this.messages = [...byId.values()].sort((a, b) => a.id - b.id);

        if (this.editingBroadcastMessage) {
            const updated = byId.get(this.editingBroadcastMessage.id);
            if (updated) {
                this.editingBroadcastMessage = updated;
            }
        }
    }

    hasAttachments(msg: BroadcastMessage): boolean {
        return (msg.attachments?.length ?? 0) > 0;
    }

    isImageAttachment(attachment: BroadcastAttachment): boolean {
        if (!attachment?.attachment) {
            return false;
        }
        const type = attachment.attachment.type?.toLowerCase();
        const name = attachment.attachment.name?.toLowerCase() || '';
        const url = attachment.attachment.url?.toLowerCase() || '';
        if (type === 'image' || type === 'gif') {
            return true;
        }
        if (url.includes('giphy.com') || url.includes('i.giphy.com')) {
            return true;
        }
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
        if (imageExtensions.some((ext) => name.endsWith(ext) || url.endsWith(ext))) {
            return true;
        }
        if (attachment.attachment.mime_type?.toLowerCase().startsWith('image/')) {
            return true;
        }
        return false;
    }

    isGifAttachment(attachment: BroadcastAttachment): boolean {
        if (!attachment?.attachment) {
            return false;
        }
        const type = attachment.attachment.type?.toLowerCase();
        const url = attachment.attachment.url?.toLowerCase() || '';
        if (url.includes('giphy.com') || url.includes('i.giphy.com')) {
            return true;
        }
        if (type === 'gif') {
            return true;
        }
        if (attachment.attachment.mime_type?.toLowerCase() === 'image/gif') {
            return true;
        }
        return url.endsWith('.gif');
    }

    isVideoAttachment(attachment: BroadcastAttachment): boolean {
        if (!attachment?.attachment) {
            return false;
        }
        const type = attachment.attachment.type?.toLowerCase();
        const mimeType = attachment.attachment.mime_type?.toLowerCase() || '';
        const name = attachment.attachment.name?.toLowerCase() || '';
        const url = attachment.attachment.url?.toLowerCase() || '';
        if (type === 'video') {
            return true;
        }
        if (mimeType.startsWith('video/')) {
            return true;
        }
        const videoExtensions = ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.webm', '.mkv'];
        return videoExtensions.some((ext) => name.endsWith(ext) || url.endsWith(ext));
    }

    getAttachmentIcon(attachment: BroadcastAttachment): string {
        const mimeType = attachment.attachment.mime_type?.toLowerCase() || '';
        const extension = attachment.attachment.extension?.toLowerCase() || '';
        if (mimeType.includes('pdf')) {
            return 'icon icon-pdf-document';
        }
        if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) {
            return 'icon icon-excel-document';
        }
        if (mimeType.includes('word') || mimeType.includes('document')) {
            return 'icon icon-word-document';
        }
        if (mimeType.includes('text/plain') || mimeType.includes('text')) {
            return 'icon icon-text-document';
        }
        if (extension === 'pdf') {
            return 'icon icon-pdf-document';
        }
        if (extension === 'xls' || extension === 'xlsx' || extension === 'csv') {
            return 'icon icon-excel-document';
        }
        if (extension === 'doc' || extension === 'docx') {
            return 'icon icon-word-document';
        }
        if (extension === 'txt') {
            return 'icon icon-text-document';
        }
        return 'icon icon-text-document';
    }

    openAttachmentPreview(attachment: BroadcastAttachment): void {
        if (!attachment?.attachment) {
            return;
        }
        if (this.isImageAttachment(attachment) && !this.isGifAttachment(attachment)) {
            this.openMediaLightbox(attachment.attachment.url, attachment.attachment.name || 'Image');
            return;
        }
        if (this.isVideoAttachment(attachment)) {
            this.openMediaLightbox(attachment.attachment.url, attachment.attachment.name || 'Video');
        }
    }

    openMediaLightbox(imageUrl: string, label: string): void {
        this.selectedMedia = { image_url: imageUrl, label };
        this.isClosingImageModal = false;
        this.showImageModal = true;
    }

    closeImageModal(): void {
        this.isClosingImageModal = true;
        setTimeout(() => {
            this.showImageModal = false;
            this.isClosingImageModal = false;
            this.selectedMedia = null;
        }, 180);
    }

    isVideoFile(media: { image_url: string } | null): boolean {
        if (!media?.image_url) {
            return false;
        }
        const url = media.image_url.toLowerCase();
        const videoExtensions = ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.webm', '.mkv'];
        return videoExtensions.some((ext) => url.includes(ext) || url.endsWith(ext));
    }
}
